import { useQuery } from '@tanstack/react-query';
import { api, type LiveSnap } from '../api';

export function LinkLivePanel({ linkId, kind = 'pop' }: { linkId: number; kind?: 'pop' | 'device' }) {
  const q = useQuery({
    queryKey: [kind === 'pop' ? 'pop-link-live' : 'device-link-live', linkId],
    queryFn: () => kind === 'pop' ? api.popLinkLive(linkId) : api.deviceLinkLive(linkId),
    refetchInterval: 15_000
  });
  if (q.isLoading) return <div className="text-xs text-slate-500 mt-3">carregando live…</div>;
  if (q.isError)   return <div className="text-xs text-rose-400 mt-3">live: {(q.error as any)?.detail?.detail ?? (q.error as any)?.message}</div>;
  const d = q.data;
  if (!d) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-800">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-300">Live · Zabbix</div>
        <div className="text-[10px] text-slate-500">{d.kind === 'no-items' ? 'sem items pra essa interface' : 'auto-refresh 15s'}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <SideLive side={d.source} label="▶ Origem" color="emerald" />
        <SideLive side={d.dest}   label="◀ Destino" color="rose" />
      </div>
    </div>
  );
}

function SideLive({ side, label, color }: { side: LiveSnap; label: string; color: 'emerald'|'rose' }) {
  const cBadge = color === 'emerald' ? 'text-emerald-400' : 'text-rose-400';
  return (
    <div className="ring-1 ring-slate-800 rounded p-2">
      <div className={`text-[11px] font-semibold ${cBadge} mb-1`}>{label}{side.ifaceName ? ` · ${side.ifaceName}` : ''}</div>
      <div className="grid grid-cols-2 gap-1 text-xs">
        <Metric label="Status" value={side.operStatus ?? '—'} highlight={side.operStatus === 'up' ? 'emerald' : side.operStatus === 'down' ? 'rose' : null} />
        <Metric label="Idade"  value={side.lastClock ? `${Math.round((Date.now()/1000 - side.lastClock))}s` : '—'} />
        <Metric label="IN"  value={fmtBps(side.trafficInBps)} />
        <Metric label="OUT" value={fmtBps(side.trafficOutBps)} />
        <Metric label="RX"  value={fmtDbm(side.rxDbm)} highlight={dbmHl(side.rxDbm)} />
        <Metric label="TX"  value={fmtDbm(side.txDbm)} highlight={dbmHl(side.txDbm)} />
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }: { label: string; value: string; highlight?: 'emerald'|'rose'|null }) {
  const c = highlight === 'emerald' ? 'text-emerald-400' : highlight === 'rose' ? 'text-rose-400' : 'text-slate-200';
  return (
    <div className="flex justify-between gap-2 px-1">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono ${c}`}>{value}</span>
    </div>
  );
}

function fmtBps(bps: number | null): string {
  if (bps == null) return '—';
  if (bps >= 1e9) return (bps / 1e9).toFixed(2) + ' Gbps';
  if (bps >= 1e6) return (bps / 1e6).toFixed(1) + ' Mbps';
  if (bps >= 1e3) return (bps / 1e3).toFixed(1) + ' Kbps';
  return Math.round(bps) + ' bps';
}
function fmtDbm(v: number | null): string {
  if (v == null) return '—';
  return v.toFixed(2) + ' dBm';
}
function dbmHl(v: number | null): 'emerald'|'rose'|null {
  if (v == null) return null;
  if (v < -25 || v > 0) return 'rose';
  if (v < -20) return null;
  return 'emerald';
}
